/**
 * The app shell: the reception frame (`concierge-desktop/screens/app-frame.md`
 * v0.4, Chosen frame), on Paper's workspace nav, rail and panel layout. The
 * employee app's Home structure: a top bar, a rail under it, the content on
 * the grey canvas.
 *
 * - `paper-workspace-nav`, pinned. Start: the mark (the operator's division
 *   logo for the current paper, else the customer's, else initials). No app
 *   switcher: how apps switch across the platform isn't decided. Centre: the
 *   search pill, which opens the command palette (⌘K). End: one Create, with
 *   Add visit, Walk-in and Add contact in its menu; the location scope (single pick);
 *   notifications; the operator, whose profile panel carries the theme.
 * - `paper-workspace-rail`: Reception's five areas in one group, folding to
 *   icons (remembered) and a drawer below 640px, opened from the bar.
 * - `paper-panel-layout`: the canvas, and one push panel host per kind
 *   (shell/push-panel). The panel rule: below the nav at the full remaining
 *   height, an always-visible Close, Escape, no blur.
 *
 * Summary is dropped. Compliance, Space requests and Reception board are
 * future areas: each opens a page that says so. Today stays routed and in
 * ⌘K, out of the rail, until its place is decided (app-frame.md, Open
 * questions).
 *
 * The same shell serves the staff app (`/all-visits`) and the preview
 * harness (`/preview/all-visits`): every link is built from the shell's own
 * route.
 */

import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CdkPortalOutlet } from '@angular/cdk/portal';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import {
  PaperAccentService,
  PaperButtonComponent,
  PaperMenuComponent,
  PaperMenuItemComponent,
  PaperNotificationPanelComponent,
  PaperNotificationTriggerComponent,
  PaperSearchPillComponent,
  PaperThemeService,
  PaperToastStackComponent,
} from '@ricoh-paper/angular';
import {
  PaperActionRegistry,
  PaperCommandPaletteComponent,
  PaperLocationScopeComponent,
  PaperProfilePanelComponent,
  PaperWorkspaceMarkComponent,
  PaperWorkspaceNavComponent,
  PaperWorkspaceRailComponent,
  type PaperProfileAction,
  type PaperProfileIdentity,
  type PaperWorkspaceRailSection,
} from '@ricoh-paper/angular/shell';
import {
  PaperPanelCanvasDirective,
  PaperPanelContentDirective,
  PaperPanelHostComponent,
  PaperPanelLayoutComponent,
} from '@ricoh-paper/angular/panels';

import { VisitorsAuthService } from '../core/auth.service';
import { CompanyService } from '../core/company.service';
import { DivisionService, frameBrand, logoForPaper } from '../core/division.service';
import { RDX_VISITORS_ENV } from '../core/env';
import { ViewerService } from '../core/viewer.service';
import { DEMO_COMPANY, DEMO_DIVISION } from '../demo/demo-data';
import { VisitorsApiService } from '../services/visitors-api.service';
import { registerFrameIcons } from './frame-icons';
import { PushPanels } from './push-panel/push-panels';

/**
 * The rail's areas, in order (app-frame.md, Rail). `match` lists the paths,
 * preview ones included, that belong to each. Icons the Paper registry lacks
 * come from `frame-icons.ts`.
 */
const AREAS = [
  {
    key: 'all-visits',
    label: $localize`:@@visitors-nav-all-visits:All visits`,
    icon: 'face',
    route: 'all-visits',
    match: ['all-visits', 'visit', 'visit-questionnaire'],
  },
  {
    key: 'address-book',
    label: $localize`:@@visitors-nav-address-book:Address book`,
    icon: 'notebook-text',
    route: 'address-book',
    match: ['address-book', 'visitor', 'new-visitor'],
  },
  {
    key: 'compliance',
    label: $localize`:@@visitors-nav-compliance:Compliance`,
    icon: 'shield-check',
    route: 'compliance',
    match: ['compliance'],
  },
  {
    key: 'space-requests',
    label: $localize`:@@visitors-nav-space-requests:Space requests`,
    icon: 'pending-actions',
    route: 'space-requests',
    match: ['space-requests'],
  },
  {
    key: 'reception-board',
    label: $localize`:@@visitors-nav-reception-board:Reception board`,
    icon: 'pin',
    route: 'reception-board',
    match: ['reception-board'],
  },
] as const;

/** Create's menu. Walk-in opens Add visit until the walk-in journey is specced. */
const CREATE_ACTIONS = [
  {
    key: 'add-visit',
    label: $localize`:@@visitors-frame-add-visit:Add visit`,
    icon: 'calendar-check',
    route: 'add-visit',
  },
  {
    key: 'walk-in',
    label: $localize`:@@visitors-walk-in:Walk-in`,
    icon: 'walk',
    route: 'add-visit',
  },
  // A person, without a visit (address-book.md, Toolbar: Add contact moves
  // here from the address book's own header). Opens the address book's editor.
  {
    key: 'add-contact',
    label: $localize`:@@visitors-add-contact:Add contact`,
    icon: 'user-plus',
    route: 'address-book?visitor=new',
  },
] as const;

@Component({
  selector: 'app-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    CdkPortalOutlet,
    PaperButtonComponent,
    PaperMenuComponent,
    PaperMenuItemComponent,
    PaperSearchPillComponent,
    PaperWorkspaceRailComponent,
    PaperNotificationPanelComponent,
    PaperNotificationTriggerComponent,
    PaperToastStackComponent,
    PaperWorkspaceNavComponent,
    PaperWorkspaceMarkComponent,
    PaperLocationScopeComponent,
    PaperProfilePanelComponent,
    PaperCommandPaletteComponent,
    PaperPanelLayoutComponent,
    PaperPanelHostComponent,
    PaperPanelContentDirective,
    PaperPanelCanvasDirective,
  ],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class ShellComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly company = inject(CompanyService);
  private readonly divisions = inject(DivisionService);
  private readonly theme = inject(PaperThemeService);
  private readonly viewerService = inject(ViewerService);
  private readonly auth = inject(VisitorsAuthService);
  protected readonly actions = inject(PaperActionRegistry);
  private readonly visitorsApi = inject(VisitorsApiService);
  private readonly accent = inject(PaperAccentService);
  protected readonly panels = inject(PushPanels);

  protected readonly notificationsOpen = signal(false);
  protected readonly createOpen = signal(false);
  /** Below 640px: the rail's drawer is open. */
  protected readonly drawerOpen = signal(false);

  private readonly createMenu = viewChild.required<PaperMenuComponent>('createMenu');

  /** `''` in the staff app, `/preview` in the harness: the shell's own route. */
  protected readonly base = (() => {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['.'], { relativeTo: this.route }),
    );
    return url === '/' ? '' : url;
  })();

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  /** The area the current page belongs to. */
  protected readonly activeKey = computed(() => {
    const path = this.url().split(/[?#]/)[0].slice(this.base.length);
    const segment = path.split('/').filter(Boolean)[0] ?? '';
    return AREAS.find((a) => (a.match as readonly string[]).includes(segment))?.key ?? null;
  });

  protected readonly railSections: readonly PaperWorkspaceRailSection[] = [
    {
      id: 'areas',
      items: AREAS.map((a) => ({
        key: a.key,
        label: a.label,
        icon: a.icon,
        route: `${this.base}/${a.route}`,
      })),
    },
  ];

  protected readonly createActions = CREATE_ACTIONS;

  // ── The nav: start ──────────────────────────────────────────────
  /**
   * The brand: the operator's division, else the customer (app-frame.md,
   * Data). Demo mode shows the demo division and customer.
   */
  private readonly brand = computed(() =>
    frameBrand(
      this.divisions.division() ?? (RDX_VISITORS_ENV.PLATFORM_MODE ? null : DEMO_DIVISION),
      this.company.company() ?? (RDX_VISITORS_ENV.PLATFORM_MODE ? null : DEMO_COMPANY),
    ),
  );
  protected readonly organisation = computed(() => this.brand().name || 'Workplace');
  /** The division's logo for the paper the mark prints on. */
  protected readonly logo = computed(() => logoForPaper(this.brand(), this.theme.isDark()));
  protected readonly mark = computed(() => initials(this.organisation()) || 'R');

  /** The mark goes to the first area. */
  protected goHome(): void {
    void this.router.navigateByUrl(`${this.base}/${AREAS[0].route}`);
  }

  protected toggleCreate(trigger: HTMLElement): void {
    const wasOpen = this.createOpen();
    this.createMenu().toggle(trigger);
    this.createOpen.set(!wasOpen);
  }

  protected create(route: string): void {
    this.createOpen.set(false);
    void this.router.navigateByUrl(`${this.base}/${route}`);
  }

  // ── The nav: end ────────────────────────────────────────────────
  /** Demo mode has no viewer query: the design's front-desk persona stands in. */
  protected readonly identity = computed<PaperProfileIdentity | null>(() => {
    const viewer = this.viewerService.viewer();
    if (viewer) {
      return {
        name: viewer.name ?? viewer.email ?? '',
        subtitle: viewer.email ?? null,
        image: viewer.picture ?? null,
      };
    }
    if (RDX_VISITORS_ENV.PLATFORM_MODE) return null;
    return {
      name: 'Ruth Walker',
      subtitle: $localize`:@@visitors-demo-persona:Front desk · demo mode`,
    };
  });

  protected readonly profileActions: readonly PaperProfileAction[] = RDX_VISITORS_ENV.PLATFORM_MODE
    ? [
        {
          id: 'sign-out',
          label: $localize`:@@visitors-log-out:Log out`,
          icon: 'logout',
          separated: true,
        },
      ]
    : [];

  protected onProfileAction(id: string): void {
    if (id === 'sign-out' && RDX_VISITORS_ENV.PLATFORM_MODE) this.auth.logout();
  }

  constructor() {
    const destroyRef = inject(DestroyRef);
    registerFrameIcons();
    // ⌘K: the areas, Create's actions, Today, and people in the address book.
    const disposeActions = this.actions.register([
      ...AREAS.map((a) => ({
        id: `nav:${a.key}`,
        label: a.label,
        group: $localize`:@@visitors-palette-go-to:Go to`,
        icon: a.icon,
        suggested: true,
        run: () => this.router.navigateByUrl(`${this.base}/${a.route}`),
      })),
      {
        id: 'nav:today',
        label: $localize`:@@visitors-nav-today:Today`,
        group: $localize`:@@visitors-palette-go-to:Go to`,
        icon: 'calendar-day',
        run: () => this.router.navigateByUrl(`${this.base}/today`),
      },
      ...CREATE_ACTIONS.map((a) => ({
        id: `create:${a.key}`,
        label: a.label,
        group: $localize`:@@visitors-palette-create:Create`,
        icon: a.icon,
        suggested: true,
        run: () => this.router.navigateByUrl(`${this.base}/${a.route}`),
      })),
    ]);
    const disposeSource = this.actions.registerSource({
      id: 'visitors',
      label: $localize`:@@visitors-palette-people:Address book`,
      search: async (query) => {
        const people = await this.visitorsApi.suggest(query);
        return people.map((v) => ({
          id: v._id,
          label: [v.firstName, v.lastName].filter(Boolean).join(' ') || v.email || v._id,
          description: v.company ?? v.email ?? undefined,
          icon: 'user',
          run: () =>
            this.router.navigate([`${this.base}/address-book`], {
              queryParams: { visitor: v._id },
            }),
        }));
      },
    });
    destroyRef.onDestroy(() => {
      disposeActions();
      disposeSource();
    });
    this.panels.markHostsMounted();

    // The Division accent wears the brand's colour: the division's, else the customer's.
    effect(() => {
      const b = this.brand();
      this.accent.setDivision(
        b.primaryColor
          ? {
              name: b.name,
              hex: b.primaryColor,
              logoLight: b.logoLight,
              logoDark: b.logoDark,
            }
          : null,
      );
    });
  }
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
